import { memo, useState, useEffect } from 'react';
import styled from 'styled-components';

import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Box,
  styledComponentsTheme,
  ChevronRightIcon,
} from '@frontend/components';
import {
  Layer,
  VECTOR_EDITOR_MODES,
} from '@frontend/partviewer/src/renderer/renderer-component';

const MoveToLayerMenu = styled.ul`
  position: absolute;
  left: 295px;
  top: 32px;
  border: 1px solid;
  border-color: var(--chakra-colors-separator-border);
  border-radius: var(--chakra-radii-md);
  box-shadow: var(--chakra-shadows-popover);
  background: var(--popover-bg);
`;

//margin should align ActionListItem's padding (negative values)
const Actions = styled.ul`
  width: 295px;
  margin: -8px -12px;
`;

type ActionColor = {
  theme: typeof styledComponentsTheme;
  delete?: boolean;
};

//padding should align Actions' margin (positive values)
const ActionListItem = styled.li<ActionColor>`
  button {
    width: 295px;
    text-align: left;
    outline: 0;
    padding: 8px 12px;
    transition: all 0.2s ease;
    ${(props) =>
      props.delete && `color: ${props.theme.color.interaction.destructive};`}

    :hover {
      cursor: pointer;
      background: #ecf6fd;
    }
    :focus,
    :active,
    :focus:active {
      background: #d2eafb;
    }
  }

  :last-of-type {
    margin-bottom: 0;
  }
`;

const MoveToButton = styled.button`
  display: flex;
  justify-content: space-between;
`;

const Spacer = styled.div`
  border-bottom: 1px solid #ddd;
  margin: 0 12px;
}
`;

const GroupTitle = styled.p`
  padding: 8px 0 0 12px;
  font-weight: var(--chakra-fontWeights-heavy);
  font-size: var(--chakra-fontSizes-sm);
  line-height: var(--chakra-lineHeights-sm);
  color: var(--chakra-colors-feature-secondary);
  text-transform: uppercase;
  cursor: default;
`;

type PopoverPosition = {
  top: number;
  left: number;
};

type HasPopoverPosition = {
  popoverPosition?: PopoverPosition;
};

const DummyDiv = styled.div<HasPopoverPosition>`
  position: fixed;

  ${(props) =>
    props.popoverPosition && props.popoverPosition.top
      ? `
      top: ${props.popoverPosition.top}px;
      left: ${props.popoverPosition.left}px;
    `
      : ''};
`;

type VectorizationPopoverOptions = {
  getAssignToLayerOptions: () => Array<Layer>;
  selectionsCount: number;
  editorMode: VECTOR_EDITOR_MODES;
  handleMoveTo: (toLayer: string) => void;
  handleOnInvert: () => void;
  handleOnIsolate: () => void;
  handleOnDelete: () => void;
  closePopover: () => void;
  isPopoverOpen?: boolean;
  popoverPosition?: PopoverPosition;
};

const VectorizationPopover = ({
  getAssignToLayerOptions,
  selectionsCount,
  editorMode,
  handleMoveTo,
  handleOnInvert,
  handleOnIsolate,
  handleOnDelete,
  closePopover,
  isPopoverOpen,
  popoverPosition,
}: VectorizationPopoverOptions) => {
  const [assignToLayerOptions, setAssignToLayerOptions] = useState<
    Array<Layer>
  >([]);
  const [moveToLayerMenu, setMoveToLayerMenu] = useState<boolean>(false);

  useEffect(() => {
    if (isPopoverOpen && selectionsCount) {
      setAssignToLayerOptions(getAssignToLayerOptions);
    }
  }, [isPopoverOpen, selectionsCount, getAssignToLayerOptions]);

  // close menu via state trigger and reset component state to prevent wrong initialized states on next render
  useEffect(() => {
    if (
      (!selectionsCount || editorMode === VECTOR_EDITOR_MODES.SCALE) &&
      isPopoverOpen
    ) {
      closePopover();
      moveToLayerMenu && setMoveToLayerMenu(false);
      assignToLayerOptions.length && setAssignToLayerOptions([]);
    }
  }, [
    isPopoverOpen,
    selectionsCount,
    editorMode,
    moveToLayerMenu,
    assignToLayerOptions,
    closePopover,
  ]);

  // if no selections or in Scale mode, don't render context menu popover
  if (!selectionsCount || editorMode === VECTOR_EDITOR_MODES.SCALE) {
    return <></>;
  }

  return (
    <Box zIndex={10}>
      <Popover
        isOpen={isPopoverOpen}
        onClose={closePopover}
        size="sm"
        placement="left-start"
        hasArrow={false}
      >
        {/* //eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore: chakra type for PopoverTrigger does not handle props.children*/}
        <PopoverTrigger>
          <DummyDiv popoverPosition={popoverPosition} />
        </PopoverTrigger>
        <PopoverContent
          borderRadius={0}
          _focusVisible={undefined}
          width="unset"
        >
          <PopoverBody>
            <Actions>
              <GroupTitle>Move To</GroupTitle>
              <ActionListItem
                onMouseEnter={() => setMoveToLayerMenu(true)}
                onMouseLeave={() => {
                  setMoveToLayerMenu(false);
                }}
                // onFocus={() => setMoveToLayerMenu(true)}
                // onBlur={() => {
                //   setMoveToLayerMenu(false);
                // }}
              >
                <MoveToButton>
                  <span>Move to layer</span>
                  <ChevronRightIcon />
                </MoveToButton>
                {moveToLayerMenu && (
                  <MoveToLayerMenu>
                    {assignToLayerOptions.map((layer) => (
                      <ActionListItem key={layer.key}>
                        <button onClick={() => handleMoveTo(layer.key)}>
                          {layer.label}
                        </button>
                      </ActionListItem>
                    ))}
                  </MoveToLayerMenu>
                )}
              </ActionListItem>
              <Spacer />
              <GroupTitle>Selection Actions</GroupTitle>
              <ActionListItem>
                <button
                  onClick={() => {
                    handleOnInvert();
                    closePopover();
                  }}
                >
                  Invert selection
                </button>
              </ActionListItem>
              <ActionListItem>
                <button onClick={handleOnIsolate}>Isolate selection</button>
              </ActionListItem>
              <ActionListItem delete>
                <button onClick={handleOnDelete}>Delete selection</button>
              </ActionListItem>
            </Actions>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default memo(VectorizationPopover);
